package top.wcpe.allincore.ui.runtime.render.layer

import top.wcpe.allincore.ui.runtime.element.Slot
import top.wcpe.allincore.ui.runtime.theme.ControlTheme
import kotlin.jvm.JvmStatic

/**
 * 槽位「背景遮罩色」解析器（issue #68）：返回物品图标背后的纯背景色，供平台层在元素 opacity < 1 时
 * 用作半透明遮罩，让 3D 方块 / 贴图规则 / bbmodel 物品随 opacity 变暗，且与平面物品变暗程度一致。
 *
 * 当背后不是单一纯色（状态纹理 / 背景纹理 / 透明槽 / 内层填充透明）时返回 0——平台层据此回退旧的
 * setShaderColor 淡化、不画遮罩，与历史行为一致（零回归）。解析逻辑与 [SlotBackgroundRenderer.render]
 * 的背景绘制保持一致（共用 [resolveStateTexture]）。
 */
@Suppress("MagicNumber")
object SlotBackdropColor {

    /**
     * 解析槽位物品背后的纯背景色（ARGB）；背后非纯色时返回 0（哨兵：平台层回退、不画遮罩）。
     */
    @JvmStatic
    fun resolve(slot: Slot, t: ControlTheme): Int {
        // 状态纹理 / 背景纹理：背后是图片而非纯色，回退。
        if (resolveStateTexture(slot) != null) {
            return 0
        }
        val bgTex = slot.backgroundTexture()
        if (!bgTex.isNullOrEmpty()) {
            return 0
        }
        // 透明槽（showBackground=false）：背后无槽位底色，回退。
        if (!slot.showBackground()) {
            return 0
        }
        // 内层填充色（物品居中落在内层之上）：覆盖优先，否则主题兜底。透明则回退。
        val inner = slot.backgroundColor() ?: t.slotBackgroundInnerColor
        return if ((inner ushr 24) == 0) 0 else inner
    }

    /**
     * 解析当前生效的状态纹理（优先级：pressed > focus > disabled > hovered），无则返回 null。
     * 与 [SlotBackgroundRenderer] 的背景绘制共用，避免重复实现。
     */
    @JvmStatic
    fun resolveStateTexture(slot: Slot): String? {
        if (slot.pressed() && slot.pressedTexture() != null) return slot.pressedTexture()
        if (slot.focused() && slot.focusTexture() != null) return slot.focusTexture()
        if (!slot.enabled() && slot.disabledTexture() != null) return slot.disabledTexture()
        if (slot.hovered() && slot.hoverTexture() != null) return slot.hoverTexture()
        return null
    }
}
